// F-148 Reviewed Stock Receiving — shared contract (design §2.1).
// Target: packages/shared (consumed by apps/admin).

export const RECEIVING_LIMITS = {
  maxLines: 500,
  maxCsvBytes: 2 * 1024 * 1024,
  maxCsvColumns: 12,
  maxQuantity: 999_999,
  maxPacks: 99_999,
  maxUnitsPerPack: 10_000,
  maxReferenceLength: 80,
  maxSupplierNameLength: 120,
  maxTextField: 200,
  maxNote: 500,
  variantSearchPageSize: 25,
  scanCameraRepeatWindowMs: 1500,
  scanAppliedCaptureMemory: 200,
} as const;

export type ReceivingUnitKind = 'unit' | 'pack';
export type ReceivingLineSource = 'manual' | 'csv' | 'scan';
export type ReceivingMatchMethod = 'barcode' | 'variant_ref' | 'manual';
export type ReceivingDraftStatus = 'open' | 'posted' | 'discarded';
export type ReceivingReferenceOrigin = 'merchant' | 'system';

export interface ReceivingAccess {
  enabled: boolean;
  canManage: boolean;
}

export interface ReceivingSupplier {
  id: string;
  name: string;
}

export type ReceivingSupplierInput =
  | { kind: 'existing'; supplierId: string }
  | { kind: 'new'; name: string };

export interface ReceivingReferenceView {
  referenceOrigin: ReceivingReferenceOrigin;
  /** Merchant-entered display text; null when origin = 'system'. */
  shipmentReference: string | null;
  /** Always present, generated once at create, e.g. "RCV-260930-7K3QD9MX". */
  systemReference: string;
  /** shipmentReference ?? systemReference */
  displayReference: string;
}

export interface ReceivingIdentifiers {
  /** Normalized (§4.3), leading zeros kept. */
  barcode: string | null;
  /** Dukanti variant UUID (evidence + store-scoped resolution). */
  variantRef: string | null;
  /** Evidence only, never matched. */
  supplierCode: string | null;
  /** Evidence only, never matched. */
  supplierItemName: string | null;
}

export interface ReceivingDraftLineInput {
  clientLineId: string;
  source: ReceivingLineSource;
  sourceRowNumber: number | null;
  identifiers: ReceivingIdentifiers;
  variantId: string | null;
  matchMethod: ReceivingMatchMethod | null;
  unitKind: ReceivingUnitKind;
  packsExpected: number | null;
  unitsPerPack: number | null;
  unitsPerPackConfirmed: number | null;
  expectedUnits: number | null;
  acceptedUnits: number | null;
  damagedUnits: number | null;
  missingUnits: number | null;
  note: string | null;
  /**
   * Persisted accepted provenance (§4.8). true = accepted is system-calculated; false = typed by
   * the merchant or supplied explicitly by CSV. Required on every save/import line; the server
   * value is authoritative after reload. Never inferred from
   * accepted === expected − damaged − missing.
   */
  acceptedAuto: boolean;
}

export interface ReceivingDraftHeaderInput {
  supplier: ReceivingSupplierInput;
  /** Blank, whitespace or invisible-only (§3.1) → null → system origin. */
  shipmentReference: string | null;
  linkedReceiptId: string | null;
  note: string | null;
}

export interface ReceivingVariantSnapshot {
  variantId: string;
  productId: string;
  productName: string;
  optionSummary: string;
  barcode: string | null;
  imagePath: string | null;
  inventory: number;
  active: boolean;
  eligible: boolean;
}

export type ReceivingLineIssueCode =
  | 'VARIANT_REQUIRED'
  | 'IDENTIFIER_UNKNOWN'
  | 'IDENTIFIER_AMBIGUOUS'
  | 'IDENTIFIER_CONFLICT'
  | 'VARIANT_INELIGIBLE'
  | 'PACK_CONFIRMATION_REQUIRED'
  | 'PACK_TOTAL_MISMATCH'
  | 'QUANTITY_MISSING'
  | 'QUANTITY_OUT_OF_RANGE'
  | 'QUANTITY_SUM_MISMATCH'
  | 'DUPLICATE_VARIANT'
  | 'INVENTORY_LIMIT_EXCEEDED';

export type ReceivingDraftIssueCode =
  | 'NO_LINES'
  | 'TOO_MANY_LINES'
  | 'REFERENCE_ALREADY_RECEIVED'
  | 'DUPLICATE_CONTENT_UNACKNOWLEDGED'
  | 'RECEIVING_DISABLED'
  | 'SIMILAR_RECENT_RECEIPT';

export interface ReceivingIssue<C extends string> {
  code: C;
  severity: 'blocking' | 'warning';
  lineNo?: number;
  field?: string;
  meta?: Record<string, string | number>;
}

export interface ReceivingDraftLine extends ReceivingDraftLineInput {
  lineNo: number;
  variant: ReceivingVariantSnapshot | null;
  candidates: {
    byBarcode: ReceivingVariantSnapshot[];
    byVariantRef: ReceivingVariantSnapshot | null;
  };
  issues: ReceivingIssue<ReceivingLineIssueCode>[];
}

export interface ReceivingDraft extends ReceivingReferenceView {
  id: string;
  storeId: string;
  status: ReceivingDraftStatus;
  version: number;
  supplier: ReceivingSupplier;
  linkedReceiptId: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  postedReceiptId: string | null;
  lines: ReceivingDraftLine[];
  issues: ReceivingIssue<ReceivingDraftIssueCode>[];
}

export interface ReceivingTotals {
  lines: number;
  expected: number;
  accepted: number;
  damaged: number;
  missing: number;
}

export interface ReceivingPreview {
  draftId: string;
  version: number;
  reviewToken: string;
  /** ready = no 'blocking' issue */
  ready: boolean;
  issues: ReceivingIssue<ReceivingDraftIssueCode | ReceivingLineIssueCode>[];
  deltas: Array<{
    variantId: string;
    productName: string;
    optionSummary: string;
    inventoryNow: number;
    acceptedUnits: number;
  }>;
  totals: ReceivingTotals;
}

export interface PostReceivingInput {
  draftId: string;
  expectedVersion: number;
  reviewToken: string;
  idempotencyKey: string;
}

export interface PostReceivingResult {
  outcome: 'posted' | 'replayed' | 'already_posted';
  receiptId: string;
}

export type CorrectionReason =
  | 'accepted_count_error'
  | 'damage_found_after_posting'
  | 'wrong_variant_received'
  | 'other';

export interface CorrectReceiptLineInput {
  receiptLineId: string;
  units: number;
  reason: CorrectionReason;
  reclassifyAs: 'missing' | 'damaged';
  note: string | null;
  idempotencyKey: string;
}

export interface StockReceiptCorrection {
  id: string;
  receiptLineId: string;
  units: number;
  reason: CorrectionReason;
  reclassifyAs: 'missing' | 'damaged';
  note: string | null;
  inventoryBefore: number;
  inventoryAfter: number;
  createdAt: string;
  createdByEmail: string;
}

export interface StockReceiptLine {
  id: string;
  lineNo: number;
  variantId: string;
  productId: string;
  productNameSnapshot: string;
  optionSummarySnapshot: string;
  barcodeSnapshot: string | null;
  identifiers: ReceivingIdentifiers;
  unitKind: ReceivingUnitKind;
  packsExpected: number | null;
  unitsPerPack: number | null;
  expectedUnits: number;
  acceptedUnits: number;
  damagedUnits: number;
  missingUnits: number;
  /** null when accepted = 0 */
  inventoryBefore: number | null;
  /** null when accepted = 0 */
  inventoryAfter: number | null;
  correctedUnits: number;
  correctableUnits: number;
  note: string | null;
}

export interface StockReceipt extends ReceivingReferenceView {
  id: string;
  storeId: string;
  draftId: string;
  supplier: { id: string; nameSnapshot: string };
  linkedReceiptId: string | null;
  postedAt: string;
  postedByEmail: string;
  totals: ReceivingTotals;
  lines: StockReceiptLine[];
  corrections: StockReceiptCorrection[];
}

export interface ReceivingListItem extends ReceivingReferenceView {
  kind: 'draft' | 'receipt';
  id: string;
  supplierName: string;
  lineCount: number;
  acceptedUnits: number | null;
  updatedAt: string;
}

/** Opaque cursor paging. */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export type ReceivingCsvFileErrorCode =
  | 'empty_file'
  | 'too_large'
  | 'unsupported_format'
  | 'utf16_encoding'
  | 'encoding_not_utf8'
  | 'invalid_character'
  | 'semicolon_delimiter'
  | 'missing_headers'
  | 'unknown_header'
  | 'duplicate_header'
  | 'too_many_rows';

export type ReceivingCsvRowErrorCode =
  | 'invalid_character'
  | 'control_character'
  | 'bad_quote'
  | 'too_many_fields'
  | 'not_integer'
  | 'out_of_range'
  | 'invalid_barcode'
  | 'invalid_uuid'
  | 'too_long'
  | 'pack_incomplete'
  | 'pack_total_mismatch'
  | 'partial_disposition'
  | 'invalid_unit_kind';

export type ReceivingScanOutcome =
  | { kind: 'added'; lineNo: number; productName: string; optionSummary: string; expectedUnits: 1 }
  | {
      kind: 'incremented';
      lineNo: number;
      productName: string;
      optionSummary: string;
      expectedUnits: number;
      acceptedUnits: number;
    }
  | { kind: 'pack_line'; lineNo: number }
  | { kind: 'duplicate_lines'; lineNos: number[] }
  | { kind: 'unresolved_added'; lineNo: number; reason: 'unknown' | 'ineligible' }
  | { kind: 'already_unresolved'; lineNo: number }
  | { kind: 'invalid' }
  | { kind: 'line_limit' }
  | { kind: 'quantity_limit'; lineNo: number }
  | { kind: 'lookup_failed' }
  /** Camera repeat within one capture: no state change, no announcement. */
  | { kind: 'ignored_repeat' };

export type StockReceivingErrorCode =
  | 'F148_NOT_FOUND'
  | 'F148_DISABLED'
  | 'F148_VALIDATION'
  | 'F148_DRAFT_EXISTS'
  | 'F148_DRAFT_VERSION_CONFLICT'
  | 'F148_DRAFT_NOT_OPEN'
  | 'F148_REVIEW_STALE'
  | 'F148_DRAFT_INVALID'
  | 'F148_REFERENCE_ALREADY_RECEIVED'
  | 'F148_IDEMPOTENCY_KEY_REUSED'
  | 'F148_VARIANT_INELIGIBLE'
  | 'F148_INVENTORY_LIMIT'
  | 'F148_STOCK_BUSY'
  | 'F148_CORRECTION_EXCEEDS_RECEIPT'
  | 'F148_CORRECTION_EXCEEDS_AVAILABLE'
  | 'F148_UNKNOWN';

// ---------------------------------------------------------------------------
// Service I/O shapes implied by design §2.2 / §4.1 / §4.2 / §4.4.
// ---------------------------------------------------------------------------

/** One validated template-v1 row (§4.1). Quantities are already resolved and in range. */
export interface ReceivingCsvRow {
  sourceRowNumber: number;
  identifiers: ReceivingIdentifiers;
  unitKind: ReceivingUnitKind;
  packsExpected: number | null;
  unitsPerPack: number | null;
  expectedUnits: number;
  acceptedUnits: number;
  damagedUnits: number;
  missingUnits: number;
  /** true when all three dispositions were blank; false when all three were explicit (§4.1). */
  acceptedAuto: boolean;
  note: string | null;
}

export interface ReceivingCsvFileMeta {
  /** ≤ 120 characters */
  fileName: string;
  byteSize: number;
  rowCount: number;
}

export interface ReceivingCsvRowError {
  /** 1-based data row number (header excluded) */
  row: number;
  /** Template column name, or null when the error is positional (e.g. too_many_fields). */
  column: string | null;
  code: ReceivingCsvRowErrorCode;
}

export type ReceivingCsvParseResult =
  | { ok: true; rows: ReceivingCsvRow[]; fileMeta: ReceivingCsvFileMeta }
  | {
      ok: false;
      fileError: ReceivingCsvFileErrorCode | null;
      /** First 50 row errors. */
      rowErrors: ReceivingCsvRowError[];
    };

export interface CreateReceivingDraftInput {
  storeId: string;
  clientRequestId: string;
  header: ReceivingDraftHeaderInput;
}

export interface SaveReceivingDraftInput {
  draftId: string;
  expectedVersion: number;
  saveToken: string;
  header: ReceivingDraftHeaderInput;
  lines: ReceivingDraftLineInput[];
}

export interface SaveReceivingDraftResult {
  version: number;
  replayed: boolean;
}

export interface ImportReceivingRowsInput {
  draftId: string;
  expectedVersion: number;
  saveToken: string;
  rows: ReceivingCsvRow[];
  fileMeta: ReceivingCsvFileMeta;
  acknowledgeDuplicate: boolean;
}

export interface ReceivingImportDuplicateMatch {
  kind: 'draft' | 'receipt';
  id: string;
  displayReference: string;
}

export type ImportReceivingRowsResult =
  | { outcome: 'imported'; version: number; replayed: boolean }
  | { outcome: 'duplicate_warning'; matches: ReceivingImportDuplicateMatch[] };

export interface DiscardReceivingDraftInput {
  draftId: string;
  expectedVersion: number;
}

export interface SearchReceivingVariantsInput {
  storeId: string;
  query: string;
  cursor: string | null;
}

export type SearchReceivingVariantsResult = Page<ReceivingVariantSnapshot> & {
  exactBarcode: ReceivingVariantSnapshot | null;
};

export interface ListReceivingInput {
  storeId: string;
  kind: 'draft' | 'receipt';
  cursor: string | null;
}

export interface CorrectReceiptLineResult {
  outcome: 'applied' | 'replayed';
  correction: StockReceiptCorrection;
}

export interface StockReceivingErrorDetails {
  [key: string]: unknown;
}
